import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Bell, Package, TrendingUp, CheckCheck, AlertCircle, MapPin, AlertTriangle, Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { settingsService } from '../services/settingsService';
import { pushNotificationService } from '../services/pushNotificationService';
import { alwaysLocationService } from '../services/alwaysLocationService';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';

interface NotificationSetting {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  icon: any;
}

export function DriverMobileNotificationsSettings() {
  const navigate = useNavigate();
  const isNativeApp = Capacitor.isNativePlatform();
  
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [locationPermission, setLocationPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [showTestNotification, setShowTestNotification] = useState(false);

  const [settings, setSettings] = useState<NotificationSetting[]>([
    {
      id: 'new-offers',
      title: 'Yeni Sevkiyatlar',
      description: 'Yeni sevkiyat açıldığında bildirim al',
      enabled: true,
      icon: Package,
    },
    {
      id: 'bid-updates',
      title: 'Teklif Güncellemeleri',
      description: 'Verdiğiniz tekliflerde değişiklik olduğunda bildirim al',
      enabled: true,
      icon: TrendingUp,
    },
    {
      id: 'bid-approval',
      title: 'Teklif Onayları',
      description: 'Teklifiniz kabul edildiğinde bildirim al',
      enabled: true,
      icon: CheckCheck,
    },
    {
      id: 'bid-rejection',
      title: 'Teklif Redleri',
      description: 'Teklifiniz reddedildiğinde bildirim al',
      enabled: true,
      icon: AlertCircle,
    },
    {
      id: 'system',
      title: 'Sistem Bildirimleri',
      description: 'Sistem güncellemeleri ve önemli duyurular',
      enabled: true,
      icon: Bell,
    },
  ]);

  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [driverUserId, setDriverUserId] = useState<string | null>(null);
  const [isCorporateMobileUser, setIsCorporateMobileUser] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      const userData = sessionStorage.getItem('driverMobileUser');

      try {
        if (userData) {
          const parsedUser = JSON.parse(userData);
          const userId = String(parsedUser.id);
          setDriverUserId(userId);
          setIsCorporateMobileUser(isCorporateExternalTransportMobileUser(parsedUser));

          const savedSettings = await settingsService.getMySettingValue(`user_${userId}_driver_notification_settings`, {
            settings: settings.map(({ id, title, description, enabled }) => ({ id, title, description, enabled })),
            soundEnabled: true,
            vibrationEnabled: true,
          });

          if (savedSettings.settings) {
            setSettings((currentSettings) => currentSettings.map((setting) => {
              const savedSetting = savedSettings.settings.find((item: any) => item.id === setting.id);
              return savedSetting ? { ...setting, enabled: savedSetting.enabled } : setting;
            }));
          }

          if (savedSettings.soundEnabled !== undefined) setSoundEnabled(savedSettings.soundEnabled);
          if (savedSettings.vibrationEnabled !== undefined) setVibrationEnabled(savedSettings.vibrationEnabled);
        }

        if (isNativeApp) {
          try {
            const permission = await PushNotifications.checkPermissions();
            setNotificationPermission(permission.receive === 'granted' ? 'granted' : permission.receive === 'denied' ? 'denied' : 'default');
          } catch {
            setNotificationPermission('default');
          }
        } else if ('Notification' in window) {
          setNotificationPermission(Notification.permission);
        }

        if (!userData || !isCorporateExternalTransportMobileUser(JSON.parse(userData))) {
          await checkLocationPermission();
        }
      } catch (loadError) {
        console.error('Failed to load notification settings:', loadError);
        setError('Bildirim ayarlari yuklenemedi');
      } finally {
        setLoading(false);
      }
    };

    void loadSettings();
  }, []);

  const checkLocationPermission = async () => {
    if (isCorporateMobileUser) return;
    if (isNativeApp) {
      const cachedLocation = localStorage.getItem('driverLocation');
      setLocationPermission(cachedLocation ? 'granted' : 'prompt');
      return;
    }

    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setLocationPermission(result.state as 'granted' | 'denied' | 'prompt');
        
        result.addEventListener('change', () => {
          setLocationPermission(result.state as 'granted' | 'denied' | 'prompt');
        });
      } catch (error) {
        console.log('Permission query not supported');
      }
    }

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        () => setLocationPermission('granted'),
        (geoError) => {
          if (geoError?.code === 1) {
            setLocationPermission('denied');
            return;
          }
          const cachedLocation = localStorage.getItem('driverLocation');
          setLocationPermission(cachedLocation ? 'granted' : 'prompt');
        },
        {
          enableHighAccuracy: false,
          timeout: 3000,
          maximumAge: 60000,
        }
      );
    }
  };

  const requestNotificationPermission = async () => {
    if (isNativeApp) {
      try {
        const permission = await PushNotifications.requestPermissions();
        const nextPermission = permission.receive === 'granted' ? 'granted' : permission.receive === 'denied' ? 'denied' : 'default';
        setNotificationPermission(nextPermission);
        if (nextPermission === 'granted') {
          await pushNotificationService.init();
          await pushNotificationService.registerCurrentToken();
          setShowTestNotification(true);
          setTimeout(() => setShowTestNotification(false), 3000);
        }
      } catch (error) {
        console.error('Native notification permission error:', error);
      }
      return;
    }

    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);

        if (permission === 'granted') {
          // Show a test notification
          showNotification('Bildirimler Aktif', 'Artık önemli güncellemelerden haberdar olacaksınız!');
          setShowTestNotification(true);
          setTimeout(() => setShowTestNotification(false), 3000);
        }
      } catch (error) {
        console.error('Notification permission error:', error);
      }
    }
  };

  const requestLocationPermission = () => {
    if (isCorporateMobileUser) return;
    const startRequest = async () => {
      if (isNativeApp) {
        await alwaysLocationService.requestAlwaysIfAvailable();
      }
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocationPermission('granted');
            
            // Save location to localStorage
            const locationData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              timestamp: new Date().toISOString(),
            };
            localStorage.setItem('driverLocation', JSON.stringify(locationData));
            
            // Show success notification
            showNotification('Konum İzni Verildi', 'Konumunuz artık takip edilebilir.');
            
            // Start watching location
            startLocationTracking();
          },
          (error) => {
            console.error('Location error:', error);
            setLocationPermission('denied');
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
          }
        );
      }
    };

    void startRequest();
  };

  const startLocationTracking = () => {
    if (isCorporateMobileUser) return;
    if ('geolocation' in navigator) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            speed: position.coords.speed,
            heading: position.coords.heading,
            timestamp: new Date().toISOString(),
          };
          localStorage.setItem('driverLocation', JSON.stringify(locationData));
          localStorage.setItem('driverLocationWatchId', watchId.toString());
        },
        (error) => {
          console.error('Location tracking error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }
  };

  const showNotification = (title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notification = new Notification(title, {
          body,
          icon: '/logo.png',
          badge: '/logo.png',
          tag: 'yuk-ortagi',
          requireInteraction: false,
          silent: !soundEnabled,
        });

        // Vibrate if enabled
        if (vibrationEnabled && 'vibrate' in navigator) {
          navigator.vibrate([200, 100, 200]);
        }

        notification.onclick = () => {
          window.focus();
          notification.close();
        };

        setTimeout(() => notification.close(), 5000);
      } catch (error) {
        console.error('Notification error:', error);
      }
    }
  };

  const toggleSetting = (id: string) => {
    setSettings(settings.map(setting =>
      setting.id === id ? { ...setting, enabled: !setting.enabled } : setting
    ));
  };

  const handleSave = () => {
    if (!driverUserId) {
      setError('Oturum bulunamadi');
      return;
    }

    const saveSettings = async () => {
      try {
        setSaving(true);
        setError('');

        await settingsService.saveMySetting(`user_${driverUserId}_driver_notification_settings`, {
          settings: settings.map(({ id, title, description, enabled }) => ({ id, title, description, enabled })),
          soundEnabled,
          vibrationEnabled,
          notificationPermission,
          locationPermission,
        }, {
          category: 'driver-notification',
          description: 'Surucu mobil bildirim ayarlari',
          updatedBy: driverUserId,
        });

        if (notificationPermission === 'granted') {
          showNotification('Ayarlar Kaydedildi', 'Bildirim tercihleriniz başarıyla güncellendi.');
        }

        navigate(-1);
      } catch (saveError) {
        console.error('Failed to save notification settings:', saveError);
        setError('Bildirim ayarlari kaydedilemedi');
      } finally {
        setSaving(false);
      }
    };

    void saveSettings();
  };

  const sendTestNotification = () => {
    if (notificationPermission === 'granted') {
      showNotification('Test Bildirimi', 'Bu bir test bildirimidir. Bildirimleriniz çalışıyor!');
      setShowTestNotification(true);
      setTimeout(() => setShowTestNotification(false), 3000);
    } else {
      requestNotificationPermission();
    }
  };

  const getPermissionStatusColor = (status: string) => {
    switch (status) {
      case 'granted':
        return 'bg-green-100 text-green-700';
      case 'denied':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getPermissionStatusText = (status: string) => {
    switch (status) {
      case 'granted':
        return 'İzin Verildi';
      case 'denied':
        return 'İzin Reddedildi';
      default:
        return 'İzin Bekleniyor';
    }
  };

  const deniedPermissionHelpText = isNativeApp ? 'Uygulama ayarlarından açın' : 'Tarayıcı ayarlarından açın';

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Bildirim Ayarları</h1>
        </div>

        <p className="text-sm text-slate-500 dark:text-blue-200">
          Almak istediğiniz bildirimleri özelleştirin
        </p>
      </div>

      <div className="px-4 pt-6 space-y-4">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        )}

        {loading && (
          <div className="bg-white rounded-xl shadow-lg p-4 flex items-center justify-center gap-3 text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <span>Ayarlar yukleniyor...</span>
          </div>
        )}

        {/* Notification Types */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-bold text-gray-900">Bildirim Türleri</h2>
            <p className="text-sm text-gray-500 mt-1">
              Hangi bildirimleri almak istediğinizi seçin
            </p>
          </div>
          <div>
            {settings.map((setting, index) => {
              const Icon = setting.icon;
              return (
                <div
                  key={setting.id}
                  className={`p-4 flex items-center gap-3 ${
                    index !== settings.length - 1 ? 'border-b' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{setting.title}</p>
                    <p className="text-sm text-gray-500">{setting.description}</p>
                  </div>
                   <label className="relative inline-flex items-center cursor-pointer">
                     <input
                       type="checkbox"
                       checked={setting.enabled}
                       onChange={() => toggleSetting(setting.id)}
                       className="sr-only peer"
                     />
                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm peer-checked:bg-green-500 peer-checked:after:shadow-md"></div>
                   </label>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sound & Vibration */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-bold text-gray-900">Ses & Titreşim</h2>
          </div>
          <div>
            <div className="p-4 flex items-center justify-between border-b">
              <div>
                <p className="font-medium text-gray-900">Bildirim Sesi</p>
                <p className="text-sm text-gray-500">Bildirim geldiğinde ses çal</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={soundEnabled}
                  onChange={() => setSoundEnabled(!soundEnabled)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm peer-checked:bg-green-500 peer-checked:after:shadow-md"></div>
              </label>
            </div>
            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Titreşim</p>
                <p className="text-sm text-gray-500">Bildirim geldiğinde titreşim</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={vibrationEnabled}
                  onChange={() => setVibrationEnabled(!vibrationEnabled)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm peer-checked:bg-green-500 peer-checked:after:shadow-md"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-bold text-gray-900">İzinler</h2>
            <p className="text-sm text-gray-500 mt-1">
              Bildirimleri ve konumu kullanmak için izin verin
            </p>
          </div>
          <div>
            {/* Notification Permission */}
            <div className="p-4 border-b">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Bildirim İzni</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Yeni sevkiyat ve teklif güncellemelerinden haberdar olmak için bildirim izni verin
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${getPermissionStatusColor(notificationPermission)}`}>
                  {getPermissionStatusText(notificationPermission)}
                </span>
                {notificationPermission !== 'granted' && (
                  <button
                    onClick={requestNotificationPermission}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    İzin Ver
                  </button>
                )}
                {notificationPermission === 'denied' && (
                  <div className="flex items-center gap-2 text-xs text-red-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span>{deniedPermissionHelpText}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Location Permission */}
            {!isCorporateMobileUser ? <div className="p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Konum İzni</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Gerçek zamanlı takip ve rota planlaması için konum bilgilerinizi paylaşın
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${getPermissionStatusColor(locationPermission)}`}>
                  {getPermissionStatusText(locationPermission)}
                </span>
                {locationPermission !== 'granted' && (
                  <button
                    onClick={requestLocationPermission}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                  >
                    İzin Ver
                  </button>
                )}
                {locationPermission === 'denied' && (
                  <div className="flex items-center justify-between gap-2 text-xs text-red-600">
                    <div className="flex items-center gap-2 min-w-0">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span className="min-w-0">{deniedPermissionHelpText}</span>
                    </div>
                    {isNativeApp && (
                      <button
                        onClick={() => void alwaysLocationService.openSettingsIfAvailable()}
                        className="shrink-0 rounded-lg border border-green-600 px-3 py-1.5 text-sm font-medium text-green-700 transition-colors hover:bg-green-50"
                      >
                        Ayarları Aç
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div> : null}
          </div>
        </div>

        {/* Test Notification */}
        {notificationPermission === 'granted' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-bold text-gray-900">Test</h2>
              <p className="text-sm text-gray-500 mt-1">
                Bildirim sisteminizi test edin
              </p>
            </div>
            <div className="p-4">
              <button
                onClick={sendTestNotification}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center gap-2"
              >
                <Bell className="w-5 h-5" />
                Test Bildirimi Gönder
              </button>
              {showTestNotification && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <CheckCheck className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-green-700 font-medium">Test bildirimi gönderildi!</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-shadow"
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>

      {/* Bottom Navigation */}
      <DriverBottomNav />
    </div>
  );
}
